import { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import io from 'socket.io-client';
import { LoginButton } from '@telegram-auth/react';

import Main from './Main';
import './index.css';

const SOCKET_SERVER_URL = "https://api.givemetraffic.ru";
const API_BASE_URL = "https://api.givemetraffic.ru";

function App() {
    const [realUserId, setRealUserId] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [userData, setUserData] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [plans, setPlans] = useState([]);
    const [socketId, setSocketId] = useState(null);

    // 1. Инициализация авторизации
    useEffect(() => {
        const initAuth = async () => {
            const sessionHash = Cookies.get('sessionHash');
            if (!sessionHash) {
                setIsLoaded(true);
                return;
            }
            try {
                const response = await fetch(`${API_BASE_URL}/auth/user-by-hash`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ hash: sessionHash }),
                });
                if (response.ok) {
                    const foundUser = await response.json();
                    setRealUserId(foundUser.user_id);
                    const savedAccountId = Cookies.get('activeAccountId');
                    // Если есть сохраненный ID аккаунта, используем его, иначе реальный ID
                    setCurrentUserId(savedAccountId ? Number(savedAccountId) : foundUser.user_id);
                } else {
                    Cookies.remove('sessionHash');
                    setIsLoaded(true);
                }
            } catch (error) {
                console.error("Ошибка авторизации:", error);
                setIsLoaded(true);
            }
        };
        initAuth();
    }, []);

    // 2. Загрузка данных пользователя, планов и списка аккаунтов
    useEffect(() => {
        if (!currentUserId || !realUserId) return;

        const fetchData = async () => {
            try {
                const sessionHash = Cookies.get('sessionHash');
                const [userRes, plansRes, accountsRes] = await Promise.all([
                    fetch(`${API_BASE_URL}/user?user_id=${currentUserId}&real_user_id=${realUserId}`, {
                        headers: { 'X-Session-Hash': sessionHash }
                    }),
                    fetch(`${API_BASE_URL}/plans`, {
                        headers: { 'X-Session-Hash': sessionHash }
                    }),
                    fetch(`${API_BASE_URL}/auth/accounts?user_id=${realUserId}`, {
                        headers: { 'X-Session-Hash': sessionHash }
                    })
                ]);

                if (userRes.ok) {
                    const data = await userRes.json();
                    if (!data.error) setUserData(data);
                    else {
                        // Если текущий аккаунт не найден (например, удален), переключаемся на личный
                        setCurrentUserId(realUserId);
                    }
                }

                if (plansRes.ok) setPlans(await plansRes.json());
                if (accountsRes.ok) setAccounts(await accountsRes.json());

            } catch (error) {
                console.error("Ошибка загрузки данных:", error);
            } finally {
                setIsLoaded(true);
            }
        };

        fetchData();
        // Сохраняем текущую позицию в куки
        Cookies.set('activeAccountId', currentUserId, { expires: 365 });

    }, [currentUserId, realUserId]);

    // 3. WebSocket логика
    useEffect(() => {
        if (!currentUserId) return;

        const socket = io(SOCKET_SERVER_URL, { transports: ['websocket'] });

        socket.on('connect', () => {
            setSocketId(socket.id);
            socket.emit('join_room', { user_id: currentUserId });
        });

        socket.on('start_parse', (data) => {
            console.log("Парсинг начался", data);
            setUserData(prev => prev ? ({
                ...prev,
                update: true
            }) : null);
        });

        socket.on('parsing_is_over', (data) => {
            console.log("Парсинг завершен", data);
            const { last_check, news: newNewsItems } = data;
            setUserData(prev => prev ? ({
                ...prev,
                update: false,
                last_check: last_check,
                news: newNewsItems
            }) : null);
        });

        return () => {
            socket.disconnect();
        };
    }, [currentUserId]);

    const handleTelegramAuth = async (data) => {
        try {
            // 1. Отправляем данные на сервер
            const response = await fetch(`${API_BASE_URL}/auth/create-session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });

            if (response.ok) {
                console.log("Авторизация на сервере успешна. Записываем куки...");

                // 2. Ставим куку
                Cookies.set('sessionHash', data.hash, { expires: 365 });

                // 3. ПРОВЕРКА: Убеждаемся, что кука записалась
                // Создаем интервал, который проверяет куку каждые 50мс
                let attempts = 0;
                const checkInterval = setInterval(() => {
                    const savedHash = Cookies.get('sessionHash');

                    // Если кука найдена и совпадает
                    if (savedHash === data.hash) {
                        clearInterval(checkInterval);
                        console.log("Кука подтверждена. Перезагрузка...");

                        // Небольшая задержка для UI, чтобы пользователь увидел, что что-то произошло
                        setTimeout(() => {
                            window.location.reload();
                        }, 100);
                    }

                    // Защита от бесконечного цикла (если за 2 секунды не записалась - форсируем)
                    attempts++;
                    if (attempts > 40) {
                        clearInterval(checkInterval);
                        console.warn("Кука не найдена, но время вышло. Форсируем перезагрузку.");
                        window.location.reload();
                    }
                }, 50);

            } else {
                console.error("Сервер отклонил авторизацию");
                alert("Ошибка авторизации. Попробуйте снова.");
            }
        } catch (error) {
            console.error("Login error:", error);
            alert("Ошибка сети. Проверьте соединение.");
        }
    };

    // Функция для переключения аккаунта, которую передадим в Main
    const handleSwitchAccount = (id) => {
        setCurrentUserId(Number(id));
        Cookies.set('activeAccountId', id, { expires: 365 });
    };

    if (!isLoaded) return <div className='loginWrapper'><span className="loaderGray loaderHome" /></div>;

    if (currentUserId && userData) {
        return (
            <div className='wrapper'>
                <Main
                    userId={currentUserId}
                    realUserId={realUserId}
                    socketId={socketId}
                    userData={userData}
                    setUserData={setUserData}
                    plans={plans}
                    accounts={accounts}
                    setAccounts={setAccounts}
                    setCurrentUserId={handleSwitchAccount} // Передаем обертку
                    setIsLoaded={setIsLoaded}
                />
            </div>
        );
    }

    return (
        <div className='wrapper'>
            <div className='loginWrapper'>
                <img src="https://gmpapp.ru/images/gmt.png" className='welcomeLogo' alt="Logo" />
                <div className='welcomeTitles'>
                    <h1 className='welcome'>Добро пожаловать!</h1>
                    <p className='welcomeSub'>Авторизуйтесь через Telegram, чтобы продолжить</p>
                </div>
                <LoginButton
                    botUsername="givemetraffic_bot"
                    lang="ru"
                    onAuthCallback={handleTelegramAuth}
                />
            </div>
        </div>
    );
}

export default App;